import React from "react";

import { readStrategyFields } from "./PaperStrategyCard";

/**
 * RetireModal — 운영 중인 paper 전략을 폐기 처리하기 위한 확인 모달. (DAS-126)
 *
 * 폐기 시점의 KPI 스냅샷(finalStats)을 paper-trader summary 에서 자동으로
 * 채우고, 사용자가 사유와 보조 통계(기간/상태 등)를 검토·수정한 뒤 확정한다.
 * 확정 시 onConfirm(reason, finalStats) 를 호출하며, 실제 API 호출/재조회는
 * 호출부(Overview/Detail 페이지)가 담당한다.
 *
 * props:
 *   - strategy: 폐기 대상 전략의 summary 객체 (paper-trader strategies[i])
 *   - submitting: 확정 API 진행 중 여부 (버튼 비활성/로딩)
 *   - error: 확정 실패 메시지 (있으면 표시)
 *   - onConfirm(reason, finalStats)
 *   - onCancel()
 */
export default class RetireModal extends React.Component {
    constructor(props) {
        super(props);
        const f = readStrategyFields(props.strategy ?? {});
        this.state = {
            reason: "",
            period: "",
            status: "",
            pnlKrw: Number.isFinite(f.pnl) ? String(Math.round(f.pnl)) : "",
            trades: Number.isFinite(f.trades) ? String(f.trades) : "",
            winRate: Number.isFinite(f.wr) ? String(f.wr) : "",
            profitFactor: f.pf >= 0 ? String(f.pf) : "",
        };
    }

    componentDidMount() {
        // 닫을 때 포커스를 되돌리기 위해 직전 포커스 요소 보관.
        this.prevFocused =
            document.activeElement instanceof HTMLElement
                ? document.activeElement
                : null;
        document.addEventListener("keydown", this.onKeyDown, true);
        if (this.reasonRef) this.reasonRef.focus();
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this.onKeyDown, true);
        // 트리거(폐기 버튼)로 포커스 복귀 — body 로 사라지지 않게.
        this.prevFocused?.focus?.();
    }

    onKeyDown = (e) => {
        if (e.key === "Escape" && !this.props.submitting) {
            this.props.onCancel?.();
            return;
        }
        // 포커스 트랩 — aria-modal 약속대로 Tab 이 모달 밖으로 나가지 않게 순환.
        if (e.key === "Tab") {
            const root = this.containerRef;
            if (!root) return;
            const focusables = Array.from(
                root.querySelectorAll(
                    'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])',
                ),
            ).filter((el) => !el.disabled && el.offsetParent !== null);
            if (focusables.length === 0) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }
    };

    set = (key) => (e) => this.setState({ [key]: e.target.value });

    // 입력된 보조 통계를 finalStats 객체로 직렬화. 빈 값은 제외하고,
    // 숫자 필드는 숫자로 변환해 RetiredStrategyCard 렌더 규칙과 호환시킨다.
    buildFinalStats = () => {
        const { period, status, pnlKrw, trades, winRate, profitFactor } = this.state;
        const out = {};
        if (period.trim()) out.period = period.trim();
        if (pnlKrw.trim() !== "" && Number.isFinite(Number(pnlKrw))) {
            out.pnl_krw = Number(pnlKrw);
        }
        if (trades.trim() !== "" && Number.isFinite(Number(trades))) {
            out.trades = Number(trades);
        }
        if (winRate.trim() !== "" && Number.isFinite(Number(winRate))) {
            out.win_rate = Number(winRate);
        }
        if (profitFactor.trim() !== "" && Number.isFinite(Number(profitFactor))) {
            out.profit_factor = Number(profitFactor);
        }
        if (status.trim()) out.status = status.trim();
        return Object.keys(out).length > 0 ? out : null;
    };

    onConfirm = () => {
        const reason = this.state.reason.trim();
        if (!reason) return;
        this.props.onConfirm?.(reason, this.buildFinalStats());
    };

    render() {
        const { strategy, submitting, error, onCancel } = this.props;
        const f = readStrategyFields(strategy ?? {});
        const id = f.id;
        const name = strategy?.name; // summary 에 없을 수 있음 (대개 id 만 표시)
        const canConfirm = this.state.reason.trim().length > 0 && !submitting;

        const numField = (label, key, placeholder) => (
            <label className="flex flex-col gap-1">
                <span className="text-xs text-content-muted">{label}</span>
                <input
                    type="text"
                    inputMode="decimal"
                    value={this.state[key]}
                    onChange={this.set(key)}
                    placeholder={placeholder}
                    disabled={submitting}
                    className="bg-surface border border-surface-border rounded px-2 py-1.5 text-sm text-content tabular-nums focus:outline-none focus:ring-2 focus:ring-brand disabled:opacity-60"
                />
            </label>
        );

        return (
            <div
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
                role="dialog"
                aria-modal="true"
                aria-label={`${id} 전략 폐기`}
            >
                {/* 백드롭 */}
                <button
                    type="button"
                    aria-label="닫기"
                    onClick={() => !submitting && onCancel?.()}
                    className="absolute inset-0 bg-surface/80 backdrop-blur-sm cursor-default"
                />

                <div
                    ref={(el) => (this.containerRef = el)}
                    className="relative w-full max-w-lg bg-surface-card border border-surface-border rounded-xl shadow-xl max-h-full overflow-y-auto"
                >
                    <div className="p-4 sm:p-5">
                        <div className="flex items-start gap-2 mb-3">
                            <span
                                aria-hidden="true"
                                className="inline-block h-2 w-2 rounded-full bg-loss mt-1.5 shrink-0"
                            />
                            <div className="min-w-0">
                                <h2 className="text-base font-semibold text-content">
                                    전략 폐기 —{" "}
                                    <span className="font-mono">{id}</span>
                                    {name && (
                                        <span className="text-content-secondary font-normal">
                                            {" "}· {name}
                                        </span>
                                    )}
                                </h2>
                                <p className="text-xs text-content-secondary mt-0.5">
                                    이 전략을 폐기 항목으로 옮깁니다. 운영 그리드에서 사라지고
                                    "폐기됨" 탭에 사유·최종 통계와 함께 표시됩니다. 폐기 후
                                    카드의 "복원" 버튼으로 되돌릴 수 있습니다.
                                </p>
                            </div>
                        </div>

                        <label className="flex flex-col gap-1 mb-4">
                            <span className="text-xs text-content-muted">
                                폐기 사유 <span className="text-loss">*</span>
                            </span>
                            <textarea
                                ref={(el) => (this.reasonRef = el)}
                                value={this.state.reason}
                                onChange={this.set("reason")}
                                disabled={submitting}
                                rows={3}
                                placeholder="예: 1주 paper trading 누적 net -661K KRW, 단독 vol5m 신호 강도 부족으로 NO-GO."
                                className="bg-surface border border-surface-border rounded px-2.5 py-2 text-sm text-content leading-relaxed focus:outline-none focus:ring-2 focus:ring-brand disabled:opacity-60 resize-y"
                            />
                        </label>

                        <div className="text-xs text-content-muted mb-2">
                            최종 통계 (폐기 시점 KPI 자동 입력 — 수정 가능)
                        </div>
                        <div className="grid grid-cols-2 gap-3 mb-1">
                            {numField("확정 손익 (KRW)", "pnlKrw", "예: -661000")}
                            {numField("거래 수", "trades", "예: 30")}
                            {numField("승률 (0~1)", "winRate", "예: 0.25")}
                            {numField("Profit Factor", "profitFactor", "예: 0.26")}
                            {numField("기간", "period", "예: 8일")}
                            {numField("상태", "status", "예: 1주 paper NO-GO")}
                        </div>
                        <p className="text-xs text-content-muted mb-4">
                            손익을 비워두면 "통합 정리(손익 없음)"로 표시됩니다.
                        </p>

                        {error && (
                            <div className="text-xs text-loss bg-loss-subtle rounded px-2.5 py-2 mb-3">
                                폐기 실패: {error}
                            </div>
                        )}

                        <div className="flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => onCancel?.()}
                                disabled={submitting}
                                className="px-3 py-2 text-sm text-content-secondary hover:text-content rounded min-h-11 disabled:opacity-60"
                            >
                                취소
                            </button>
                            <button
                                type="button"
                                onClick={this.onConfirm}
                                disabled={!canConfirm}
                                className="px-4 py-2 text-sm font-medium text-content-inverse bg-loss rounded min-h-11 disabled:opacity-50 hover:opacity-90 transition-opacity"
                            >
                                {submitting ? "폐기 중…" : "폐기 확정"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
